/**
 * เลือกท่าและเฟรม (PHYSICS.md §7)
 * walk frame ผูกกับ "ระยะทาง" ไม่ใช่เวลา — เดินเร็วขาถี่ขึ้นเองตามธรรมชาติ
 */
import { type AnimName, FRAMES_BY_ANIM, PHYS, type SceneMode } from "@py/protocol";

export function pickAnim(mode: SceneMode, vx: number, vy: number, onGround: boolean): AnimName {
  if (mode === "topdown") return Math.hypot(vx, vy) > 10 ? "walk" : "idle";
  if (vy < -40) return "jump";
  if (vy > 40) return "fall";
  return onGround && Math.abs(vx) > 20 ? "walk" : "idle";
}

export type WalkCycle = {
  walked: number;
  anim: string;
  /** ท่า action ที่กำลังเล่นทับอยู่ (ต่อย/เตะ) — null คือเล่นท่าปกติ */
  actAnim: "punch" | "kick" | null;
  /** เวลาที่เหลือของ action */
  actMs: number;
};

export function newWalkCycle(): WalkCycle {
  return { walked: 0, anim: "idle", actAnim: null, actMs: 0 };
}

/** เวลาต่อเฟรมของท่าที่ไม่ได้ผูกกับระยะทาง (กระโดด/ตก/ต่อย/เตะ) */
const AIR_FRAME_MS = 90;

/** ความยาวท่า action — 4 เฟรม × 90ms · จบแล้วกลับท่าปกติเอง */
export const ACTION_MS = AIR_FRAME_MS * 4;

/**
 * สั่งเล่นท่าต่อย/เตะทับท่าปกติชั่วคราว
 * กดซ้ำระหว่างเล่น = เริ่มท่าใหม่ (ต่อยรัวได้ ไม่ต้องรอจบ)
 */
export function startAction(wc: WalkCycle, act: "punch" | "kick"): void {
  wc.actAnim = act;
  wc.actMs = ACTION_MS;
  wc.anim = act;
  wc.walked = 0;
}

/**
 * ท่าที่จะใช้จริงเฟรมนี้ — action ชนะท่าจากฟิสิกส์จนกว่าจะหมดเวลา
 * เรียกก่อน stepWalkCycle ทุกเฟรม (ฝั่งจอเป็นคนเดียวที่นับเวลา)
 */
export function resolveAnim(wc: WalkCycle, base: AnimName, dtMs: number): AnimName {
  if (!wc.actAnim) return base;
  wc.actMs -= dtMs;
  if (wc.actMs <= 0) {
    wc.actAnim = null;
    return base;
  }
  return wc.actAnim;
}

/**
 * @param dtMs เวลาที่ผ่านไป
 * @returns ดัชนีเฟรมใน sheet ตาม `FRAMES_BY_ANIM`
 *
 * ท่าเดินผูกกับ **ระยะทาง** เดินเร็วขาถี่ขึ้นเองตามธรรมชาติ
 * ท่ากระโดด/ตกผูกกับ **เวลา** เพราะระยะทางแนวนอนอาจเป็นศูนย์ (กระโดดอยู่กับที่)
 * แล้วเฟรมจะค้างอยู่เฟรมเดียวตลอดการลอย
 */
export function stepWalkCycle(
  wc: WalkCycle,
  anim: AnimName,
  speed: number,
  dtMs: number,
): number {
  // เปลี่ยนท่าเมื่อไหร่ต้องเริ่มนับใหม่ ไม่งั้นกระโดดครั้งที่สองเริ่มที่เฟรมสุดท้ายเลย
  if (wc.anim !== anim) { wc.anim = anim; wc.walked = 0; }
  const frames = FRAMES_BY_ANIM[anim] ?? FRAMES_BY_ANIM["idle"]!;
  if (frames.length === 1) {
    wc.walked = 0;
    return frames[0]!;
  }
  if (anim === "walk") {
    wc.walked += Math.abs(speed) * (dtMs / 1000);
    return frames[Math.floor(wc.walked / PHYS.STRIDE_PX) % frames.length]!;
  }
  // ท่าลอยกลางอากาศ/ต่อย/เตะ — เดินหน้าตามเวลาแล้วค้างที่เฟรมสุดท้าย ไม่วนซ้ำ
  // วนซ้ำจะเห็นตัวละคร "กระพือ" ตอนตกนานๆ ซึ่งดูผิดธรรมชาติ
  wc.walked += dtMs;
  const i = Math.min(frames.length - 1, Math.floor(wc.walked / AIR_FRAME_MS));
  return frames[i]!;
}

/** หันหน้าตาม vx เท่านั้น — โหมด topdown เดินขึ้นลงไม่ควรทำให้ตัวกลับด้าน */
export function facingFrom(vx: number, current: number): number {
  if (vx > 20) return 1;
  if (vx < -20) return -1;
  return current;
}
